import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { parse, stringify } from "yaml";

import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { TabPanel, TabView } from "primereact/tabview";

import { PageProps } from "../../../models/routing.model";
import UiButton from "../../../components/button/Button";
import UiCard from "../../../components/card/Card";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import VariableInput, { VariableGroup } from "../../../components/variable-input/VariableInput";
import ConditionsBuilder from "../../../components/conditions-builder/ConditionsBuilder";
import {
	CAMPAIGN_CHANNELS,
	CampaignConfig,
	CampaignMessage,
	MarketingCampaignAdmin,
	emptyCampaign,
	emptyCampaignMessage,
	formatChannelLabel,
	parseCampaignContent,
	serializeCampaignContent,
} from "../../../models/entities/marketing-campaign-admin.model";
import {
	BuilderNode,
	builderModelToYamlNode,
	isDateLikeType,
	newConditionId,
	yamlNodeToBuilderModel,
} from "../../../utils/conditions";
import { useMarketingCampaignsAdmin } from "../../../hooks/useMarketingCampaignsAdmin";
import { useMessageTemplates, useTemplateVariables } from "../../../hooks/useMessageTemplates";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { CampaignSimulationResult } from "../../../models/entities/campaign-simulation.model";

import "./marketing-campaigns.css";

const LIST_ROUTE = "/settings/marketing-campaigns";

const conditionsToText = (conditions: unknown): string =>
	conditions !== null && conditions !== undefined ? stringify(conditions) : "";

/** Whether a parsed conditions value actually carries any rules. */
const hasConditions = (conditions: unknown): boolean =>
	conditions !== null &&
	conditions !== undefined &&
	!(Array.isArray(conditions) && conditions.length === 0);

/** The three-track state behind one conditions editor (builder + YAML fallback). */
interface ConditionsState {
	model: BuilderNode | null;
	text: string;
	advanced: boolean;
}

/** Seed a conditions editor from a loaded YAML value. */
const initConditions = (conditions: unknown): ConditionsState => {
	const model = yamlNodeToBuilderModel(conditions);
	const present = hasConditions(conditions);
	return {
		model,
		text: present ? conditionsToText(conditions) : "",
		// Fall back to raw YAML only when the builder can't represent real rules.
		advanced: present && model === null,
	};
};

/**
 * Resolve a conditions editor to the YAML-serializable value to store: raw YAML
 * in advanced mode (throws on invalid YAML, surfaced to the user), otherwise the
 * builder model. Returns undefined when there are no conditions.
 */
const conditionsNode = (state: ConditionsState): unknown => {
	if (state.advanced) {
		return state.text && state.text.trim() ? parse(state.text) : undefined;
	}
	return builderModelToYamlNode(state.model);
};

const messageConditionsState = (message: CampaignMessage): ConditionsState => ({
	model: message.__conditionsModel ?? null,
	text: message.__conditionsText ?? "",
	advanced: !!message.__conditionsAdvanced,
});

/**
 * Re-serialize the structured editor state back to a campaign YAML string.
 * Conditions come from the visual builder, or from raw YAML when an editor is in
 * advanced mode (which throws if that YAML is invalid — surfaced to the user).
 */
const buildCampaignContent = (
	config: CampaignConfig,
	campaignConditions: ConditionsState
): string => {
	const cleanMessages = config.messages.map((message) => {
		const node = conditionsNode(messageConditionsState(message));
		const clean: CampaignMessage = { ...message };
		delete clean.__uiId;
		delete clean.__conditionsModel;
		delete clean.__conditionsText;
		delete clean.__conditionsAdvanced;
		if (node !== undefined) clean.conditions = node;
		else delete clean.conditions;
		return clean;
	});

	const cleanConfig: CampaignConfig = { ...config, messages: cleanMessages };
	const node = conditionsNode(campaignConditions);
	if (node !== undefined) cleanConfig.conditions = node;
	else delete cleanConfig.conditions;
	return serializeCampaignContent(cleanConfig);
};

const SettingsMarketingCampaignEdit = (props: PageProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const params = useParams<{ campaignId: string }>();
	const toast = useRef<Toast>(null);
	// Save errors are shown inline (like ParticipantsCreateModal), not as a toast. A
	// successful save is silent. The toast above is still used by the testing tab.
	const [saveMessage, setSaveMessage] = useState<string | null>(null);

	// The "new" page is served by a dedicated static route (marketing-campaigns/new)
	// which has no :campaignId param, so params.campaignId is undefined there — treat
	// that as the new route too (otherwise it tries to load campaign NaN → "not found").
	const isNewRoute = params.campaignId === "new" || !params.campaignId;
	const numericId = isNewRoute ? null : Number(params.campaignId);

	const campaigns = useMarketingCampaignsAdmin();
	const messageTemplates = useMessageTemplates();
	const templateVariables = useTemplateVariables();

	const [draft, setDraft] = useState<MarketingCampaignAdmin | null>(null);
	const [config, setConfig] = useState<CampaignConfig | null>(null);
	const [campaignConditions, setCampaignConditions] = useState<ConditionsState>({
		model: null,
		text: "",
		advanced: false,
	});
	const [messageCollapsed, setMessageCollapsed] = useState<Map<string, boolean>>(new Map());
	const [original, setOriginal] = useState<{ name: string; content: string } | null>(null);

	const [isSaving, setIsSaving] = useState(false);

	// Testing tab: simulate which messages would be sent for a participant.
	const [testParticipantId, setTestParticipantId] = useState("");
	const [isSimulating, setIsSimulating] = useState(false);
	const [simResult, setSimResult] = useState<CampaignSimulationResult | null>(null);

	const isNew = draft !== null && draft.id === null;

	// Bootstrap from the URL: blank ("new") or hydrate from the cached list.
	useEffect(() => {
		if (config !== null) return;
		let base: MarketingCampaignAdmin | null = null;
		if (isNewRoute) {
			base = emptyCampaign();
		} else if (numericId !== null && !Number.isNaN(numericId) && campaigns.data) {
			base = campaigns.data.find((campaign) => campaign.id === numericId) ?? null;
		}
		if (!base) return;

		const parsed = parseCampaignContent(base.content);
		parsed.messages = parsed.messages.map((message) => {
			const state = initConditions(message.conditions);
			return {
				...message,
				__uiId: newConditionId(),
				__conditionsModel: state.model,
				__conditionsText: state.text,
				__conditionsAdvanced: state.advanced,
			};
		});
		const campaignState = initConditions(parsed.conditions);

		setDraft(base);
		setConfig(parsed);
		setCampaignConditions(campaignState);
		setMessageCollapsed(new Map());
		setOriginal({ name: base.name, content: buildCampaignContent(parsed, campaignState) });
	}, [config, isNewRoute, numericId, campaigns.data]);

	const templateNameOptions = useMemo(() => {
		const names = new Set((messageTemplates.data ?? []).map((template) => template.name));
		return Array.from(names)
			.sort()
			.map((name) => ({ label: name, value: name }));
	}, [messageTemplates.data]);

	const channelOptions = useMemo(
		() => CAMPAIGN_CHANNELS.map((c) => ({ label: formatChannelLabel(c), value: c })),
		[]
	);

	// Include the message's current channel even if it's a legacy value outside
	// the known set, so an existing campaign never shows a blank channel.
	const channelOptionsFor = (current?: string) => {
		if (current && !CAMPAIGN_CHANNELS.includes(current)) {
			return [...channelOptions, { label: formatChannelLabel(current), value: current }];
		}
		return channelOptions;
	};

	// Participant (user_state) columns used as fields and {{variables}}.
	const participantVariables = useMemo(
		() => (templateVariables.data ?? []).map((v) => ({ name: v.name, type: v.type })),
		[templateVariables.data]
	);

	// Template-time placeholders resolved per message (not user_state columns).
	const campaignVariableNames = useMemo(() => ["campaign", "name", "message_date"], []);

	// {{variable}} groups offered by the inserter on free-text properties.
	const variableGroups = useMemo<VariableGroup[]>(
		() => [
			{
				label: t("MARKETING_CAMPAIGNS.VARIABLES.GROUP_CAMPAIGN"),
				items: campaignVariableNames.map((name) => ({ name })),
			},
			{
				label: t("MARKETING_CAMPAIGNS.VARIABLES.GROUP_PARTICIPANT"),
				items: participantVariables,
			},
		],
		[t, campaignVariableNames, participantVariables]
	);

	// Names highlighted as "known" (green) across every editor.
	const knownVariableNames = useMemo(
		() => [...campaignVariableNames, ...participantVariables.map((v) => v.name)],
		[campaignVariableNames, participantVariables]
	);

	// Date/time-like participant columns (timestamps, dates, times) offered as
	// `relative_to` anchors — anything a relative offset can be measured from.
	const dateLikeParticipantVars = useMemo(
		() => participantVariables.filter((v) => isDateLikeType(v.type)),
		[participantVariables]
	);

	// Grouped `relative_to` options for a given message: anchors, participant
	// timestamps, and references to the campaign's other messages.
	const relativeToOptions = (index: number) => {
		const campaignKey = (config?.campaign as string) ?? "";
		const others = (config?.messages ?? [])
			.filter((_, i) => i !== index)
			.map((message) => message.name)
			.filter((name) => !!name);
		return [
			{
				label: t("MARKETING_CAMPAIGNS.RELATIVE_TO.GROUP_BASE"),
				items: [
					{
						label: t("MARKETING_CAMPAIGNS.RELATIVE_TO.CAMPAIGN_LAUNCH"),
						value: "campaign_launch",
					},
					{ label: "message_date", value: "message_date" },
				],
			},
			{
				label: t("MARKETING_CAMPAIGNS.RELATIVE_TO.GROUP_PARTICIPANT"),
				items: dateLikeParticipantVars.map((v) => ({ label: v.name, value: v.name })),
			},
			{
				label: t("MARKETING_CAMPAIGNS.RELATIVE_TO.GROUP_MESSAGES"),
				items: others.map((name) => {
					const value = campaignKey ? `${campaignKey}.${name}` : name;
					return { label: value, value };
				}),
			},
		].filter((group) => group.items.length > 0);
	};

	// Current serialized content (null when a conditions block is invalid YAML).
	const currentContent = useMemo(() => {
		if (!config) return null;
		try {
			return buildCampaignContent(config, campaignConditions);
		} catch {
			return null;
		}
	}, [config, campaignConditions]);

	const isDirty =
		!!original &&
		!!draft &&
		(draft.name !== original.name ||
			currentContent === null ||
			currentContent !== original.content);

	/* Mutators */

	const updateConfig = (changes: Partial<CampaignConfig>) =>
		setConfig((current) => (current ? { ...current, ...changes } : current));

	const updateMessage = (index: number, changes: Partial<CampaignMessage>) =>
		setConfig((current) =>
			current
				? {
						...current,
						messages: current.messages.map((message, i) =>
							i === index ? { ...message, ...changes } : message
						),
					}
				: current
		);

	const addMessage = () => {
		if (!config) return;
		const message: CampaignMessage = {
			...emptyCampaignMessage(),
			__uiId: newConditionId(),
		};
		// New cards open expanded so the user can fill them in straight away.
		setMessageCollapsed((prev) => new Map(prev).set(message.__uiId as string, false));
		setConfig({ ...config, messages: [...config.messages, message] });
	};

	const duplicateMessage = (index: number) => {
		if (!config) return;
		const source = config.messages[index];
		const copy: CampaignMessage = {
			...source,
			name: `${source.name}_copy`,
			__uiId: newConditionId(),
		};
		setMessageCollapsed((prev) =>
			new Map(prev).set(copy.__uiId as string, prev.get(source.__uiId as string) ?? true)
		);
		const messages = [...config.messages];
		messages.splice(index + 1, 0, copy);
		setConfig({ ...config, messages });
	};

	const removeMessage = (index: number) =>
		setConfig((current) =>
			current
				? { ...current, messages: current.messages.filter((_, i) => i !== index) }
				: current
		);

	/* Per-message collapse (UI-only; defaults to collapsed, keyed by stable id). */

	const collapseKey = (message: CampaignMessage, index: number): string =>
		(message.__uiId as string) ?? `idx:${index}`;

	const isMessageCollapsed = (message: CampaignMessage, index: number): boolean =>
		messageCollapsed.get(collapseKey(message, index)) ?? true;

	const toggleMessageCollapsed = (message: CampaignMessage, index: number) =>
		setMessageCollapsed((prev) => {
			const key = collapseKey(message, index);
			const next = new Map(prev);
			next.set(key, !(prev.get(key) ?? true));
			return next;
		});

	/* Navigation / persistence */

	const goBackToList = () => navigate(LIST_ROUTE);

	const confirmDiscardIfDirty = (proceed: () => void) => {
		if (!isDirty) {
			proceed();
			return;
		}
		confirmDialog({
			message: t("MARKETING_CAMPAIGNS.CONFIRM.DISCARD_MESSAGE"),
			header: t("MARKETING_CAMPAIGNS.CONFIRM.DISCARD_HEADER"),
			icon: "pi pi-exclamation-triangle",
			acceptLabel: t("MARKETING_CAMPAIGNS.CONFIRM.DISCARD_ACCEPT"),
			rejectLabel: t("MARKETING_CAMPAIGNS.CONFIRM.CANCEL"),
			accept: proceed,
		});
	};

	const onSave = async () => {
		if (!draft || !config) return;
		setSaveMessage(null);
		if (!draft.name.trim()) {
			setSaveMessage("MARKETING_CAMPAIGNS.VALIDATION.NAME_REQUIRED");
			return;
		}

		let content: string;
		try {
			content = buildCampaignContent(config, campaignConditions);
		} catch {
			setSaveMessage("MARKETING_CAMPAIGNS.TOAST.INVALID_YAML");
			return;
		}

		const body: MarketingCampaignAdmin = { ...draft, content };
		setIsSaving(true);
		try {
			const saved = isNew
				? await EndpointsService.dataRetriever.createMarketingCampaign({ body })
				: await EndpointsService.dataRetriever.updateMarketingCampaign({
						id: draft.id as number,
						body,
					});
			await campaigns.invalidate();
			setDraft({ ...draft, id: saved.id, name: saved.name });
			setOriginal({ name: saved.name, content });
			if (isNew && saved.id !== null && saved.id !== undefined) {
				navigate(`${LIST_ROUTE}/${saved.id}`, { replace: true });
			}
		} catch {
			setSaveMessage("MARKETING_CAMPAIGNS.TOAST.SAVE_ERROR");
		} finally {
			setIsSaving(false);
		}
	};

	/* Renderers */

	const renderHeader = () => (
		<div className="mc-editor-header-actions">
			{isDirty && (
				<span className="mc-dirty-badge">{t("MARKETING_CAMPAIGNS.EDITOR.UNSAVED")}</span>
			)}
			<UiButton
				type="button"
				className="p-button-rounded p-button-sm p-button-outlined"
				icon="pi pi-arrow-left"
				label="MARKETING_CAMPAIGNS.ACTIONS.BACK"
				onClick={() => confirmDiscardIfDirty(goBackToList)}
			/>
		</div>
	);

	/* Testing tab */

	const runSimulation = async () => {
		if (!config) return;
		const pid = Number(testParticipantId);
		if (!testParticipantId.trim() || Number.isNaN(pid)) {
			toast.current?.show({
				severity: "warn",
				summary: t("MARKETING_CAMPAIGNS.TOAST.VALIDATION"),
				detail: t("MARKETING_CAMPAIGNS.TESTING.PARTICIPANT_REQUIRED"),
			});
			return;
		}
		if (currentContent === null) {
			toast.current?.show({
				severity: "error",
				summary: t("MARKETING_CAMPAIGNS.TOAST.INVALID_YAML"),
			});
			return;
		}
		setIsSimulating(true);
		try {
			const result = await EndpointsService.dataRetriever.simulateCampaign({
				body: { participantId: pid, content: currentContent },
			});
			setSimResult(result);
		} catch (error) {
			setSimResult(null);
			toast.current?.show({
				severity: "error",
				summary: t("MARKETING_CAMPAIGNS.TESTING.RUN_ERROR"),
				detail: (error as AxiosError)?.message,
			});
		} finally {
			setIsSimulating(false);
		}
	};

	const formatWindowDate = (value: string | null): string =>
		value ? value.replace("T", " ").slice(0, 16) : "—";

	const renderTestingTab = () => {
		if (!config) return null;
		return (
			<div className="mc-testing">
				<p className="mc-field-help">{t("MARKETING_CAMPAIGNS.TESTING.HELP")}</p>
				<div className="mc-testing-controls">
					<span className="p-input-icon-left">
						<i className="pi pi-user" />
						<InputText
							value={testParticipantId}
							keyfilter="pint"
							onChange={(event) => setTestParticipantId(event.target.value)}
							placeholder={t("MARKETING_CAMPAIGNS.TESTING.PARTICIPANT_PLACEHOLDER")}
						/>
					</span>
					<UiButton
						type="button"
						className="p-button-rounded"
						icon="pi pi-play"
						label="MARKETING_CAMPAIGNS.TESTING.RUN"
						onClick={runSimulation}
						loading={isSimulating}
					/>
				</div>

				{simResult && (
					<>
						<div
							className={`mc-test-banner ${
								simResult.campaignValid
									? "mc-test-banner--ok"
									: "mc-test-banner--bad"
							}`}
						>
							<i
								className={`pi ${
									simResult.campaignValid ? "pi-check-circle" : "pi-times-circle"
								}`}
							/>
							<span>
								{simResult.campaignValid
									? t("MARKETING_CAMPAIGNS.TESTING.CAMPAIGN_VALID")
									: t("MARKETING_CAMPAIGNS.TESTING.CAMPAIGN_INVALID")}
							</span>
						</div>

						<div className="mc-test-results">
							{simResult.messages.map((message, index) => (
								<div
									key={index}
									className={`mc-test-msg ${message.willSend ? "mc-test-msg--send" : ""}`}
								>
									<div className="mc-test-msg-head">
										<span
											className={`mc-test-badge ${
												message.willSend
													? "mc-test-badge--send"
													: message.alreadySent
														? "mc-test-badge--sent"
														: "mc-test-badge--no"
											}`}
										>
											{message.willSend
												? t("MARKETING_CAMPAIGNS.TESTING.WILL_SEND")
												: message.alreadySent
													? t("MARKETING_CAMPAIGNS.TESTING.ALREADY_SENT")
													: t("MARKETING_CAMPAIGNS.TESTING.WONT_SEND")}
										</span>
										<span className="mc-test-msg-name">
											{message.name ||
												t("MARKETING_CAMPAIGNS.MESSAGES.UNNAMED")}
										</span>
										<span className="mc-test-msg-channel">
											{formatChannelLabel(message.channel)}
										</span>
									</div>
									<div className="mc-test-msg-reason">{message.reason}</div>
									{message.ref && (
										<div className="mc-test-msg-meta">
											{t("MARKETING_CAMPAIGNS.TESTING.REF")}:{" "}
											<code>{message.ref}</code>
										</div>
									)}
									{(message.sendDate || message.maxDate) && (
										<div className="mc-test-msg-meta">
											{t("MARKETING_CAMPAIGNS.TESTING.WINDOW")}:{" "}
											{formatWindowDate(message.sendDate)} →{" "}
											{formatWindowDate(message.maxDate)}
										</div>
									)}
								</div>
							))}
							{simResult.messages.length === 0 && (
								<p className="mc-field-help">
									{t("MARKETING_CAMPAIGNS.TESTING.NO_MESSAGES")}
								</p>
							)}
						</div>
					</>
				)}

				{!simResult && (
					<p className="mc-field-help">{t("MARKETING_CAMPAIGNS.TESTING.EMPTY")}</p>
				)}
			</div>
		);
	};

	const renderConfigTab = () => {
		if (!draft || !config) return null;
		return (
			<div className="row">
				<div className="col-12 col-md-6 mc-field">
					<label
						className="mc-field-label"
						htmlFor="mc-name"
					>
						{t("MARKETING_CAMPAIGNS.FIELDS.NAME")}
					</label>
					<InputText
						id="mc-name"
						value={draft.name}
						onChange={(event) => setDraft({ ...draft, name: event.target.value })}
					/>
				</div>
				<div className="col-12 col-md-6 mc-field">
					<label
						className="mc-field-label"
						htmlFor="mc-campaign"
					>
						{t("MARKETING_CAMPAIGNS.FIELDS.CAMPAIGN")}
					</label>
					<InputText
						id="mc-campaign"
						value={(config.campaign as string) ?? ""}
						onChange={(event) => updateConfig({ campaign: event.target.value })}
						placeholder={t("MARKETING_CAMPAIGNS.FIELDS.CAMPAIGN_PLACEHOLDER")}
					/>
				</div>
				<div className="col-12 col-md-6 mc-field">
					<label
						className="mc-field-label"
						htmlFor="mc-launch"
					>
						{t("MARKETING_CAMPAIGNS.FIELDS.CAMPAIGN_LAUNCH")}
					</label>
					<InputText
						id="mc-launch"
						value={(config.campaign_launch as string) ?? ""}
						onChange={(event) => updateConfig({ campaign_launch: event.target.value })}
						placeholder="2025-01-01T00:00:00"
					/>
				</div>
				<div className="col-12 col-md-6 mc-field">
					<label
						className="mc-field-label"
						htmlFor="mc-ref"
					>
						{t("MARKETING_CAMPAIGNS.FIELDS.REF_TEMPLATE")}
					</label>
					<VariableInput
						id="mc-ref"
						value={(config.ref_template as string) ?? ""}
						onChange={(value) => updateConfig({ ref_template: value })}
						groups={variableGroups}
						knownVariables={knownVariableNames}
						placeholder="{{campaign}}.{{name}}"
					/>
				</div>
				<div className="col-12 mc-field">
					<label className="mc-field-label">
						{t("MARKETING_CAMPAIGNS.FIELDS.CONDITIONS")}
					</label>
					<ConditionsBuilder
						value={campaignConditions.model}
						onChange={(model) => setCampaignConditions((prev) => ({ ...prev, model }))}
						rawYaml={campaignConditions.text}
						onRawYamlChange={(text) =>
							setCampaignConditions((prev) => ({ ...prev, text }))
						}
						isAdvanced={campaignConditions.advanced}
						onToggleAdvanced={(advanced) =>
							setCampaignConditions((prev) => ({ ...prev, advanced }))
						}
						fields={participantVariables}
						knownVariables={knownVariableNames}
					/>
					<small className="mc-field-help">
						{t("MARKETING_CAMPAIGNS.FIELDS.CONDITIONS_HELP")}
					</small>
				</div>
			</div>
		);
	};

	const renderMessage = (message: CampaignMessage, index: number) => {
		const collapsed = isMessageCollapsed(message, index);
		const conditionsState = messageConditionsState(message);
		const summary = [message.channel, message.offset, message.template]
			.filter((part) => !!part)
			.join(" · ");
		return (
			<div
				className="mc-message"
				key={(message.__uiId as string) ?? index}
			>
				<div className="mc-message-header">
					<button
						type="button"
						className="mc-message-toggle"
						aria-expanded={!collapsed}
						onClick={() => toggleMessageCollapsed(message, index)}
					>
						<i
							className={`pi ${collapsed ? "pi-chevron-right" : "pi-chevron-down"} mc-message-chevron`}
						/>
						<span className="mc-message-title">
							{message.name || t("MARKETING_CAMPAIGNS.MESSAGES.UNNAMED")}
						</span>
						{summary && <span className="mc-message-summary">{summary}</span>}
					</button>
					<div className="mc-message-actions">
						<UiButton
							type="button"
							className="p-button-text p-button-sm"
							icon="pi pi-copy"
							tooltip="MARKETING_CAMPAIGNS.MESSAGES.DUPLICATE"
							onClick={() => duplicateMessage(index)}
						/>
						<UiButton
							type="button"
							className="p-button-text p-button-sm neutral"
							icon="pi pi-trash"
							tooltip="MARKETING_CAMPAIGNS.MESSAGES.REMOVE"
							onClick={() => removeMessage(index)}
						/>
					</div>
				</div>
				{!collapsed && (
					<div className="row">
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.NAME")}
							</label>
							<InputText
								value={message.name}
								onChange={(event) =>
									updateMessage(index, { name: event.target.value })
								}
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.TEMPLATE")}
							</label>
							<Dropdown
								value={message.template}
								options={templateNameOptions}
								editable
								filter
								onChange={(event) =>
									updateMessage(index, { template: event.value })
								}
								placeholder={t("MARKETING_CAMPAIGNS.MESSAGES.TEMPLATE_PLACEHOLDER")}
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.CHANNEL")}
							</label>
							<Dropdown
								value={message.channel}
								options={channelOptionsFor(message.channel)}
								onChange={(event) => updateMessage(index, { channel: event.value })}
							/>
						</div>
						<div className="col-6 col-md-3 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.OFFSET")}
							</label>
							<InputText
								value={message.offset}
								onChange={(event) =>
									updateMessage(index, { offset: event.target.value })
								}
								placeholder="5d"
							/>
						</div>
						<div className="col-6 col-md-3 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.MAX_OFFSET")}
							</label>
							<InputText
								value={message.max_offset ?? ""}
								onChange={(event) =>
									updateMessage(index, { max_offset: event.target.value || null })
								}
								placeholder="7d"
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.RELATIVE_TO")}
							</label>
							<Dropdown
								value={message.relative_to}
								options={relativeToOptions(index)}
								optionGroupLabel="label"
								optionGroupChildren="items"
								editable
								filter
								onChange={(event) =>
									updateMessage(index, { relative_to: event.value })
								}
								placeholder="campaign_launch"
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.SUBJECT")}
							</label>
							<VariableInput
								value={message.subject ?? ""}
								onChange={(value) =>
									updateMessage(index, { subject: value || null })
								}
								groups={variableGroups}
								knownVariables={knownVariableNames}
							/>
						</div>
						<div className="col-12 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.PREVIEW_TEXT")}
							</label>
							<VariableInput
								value={message.preview_text ?? ""}
								onChange={(value) =>
									updateMessage(index, { preview_text: value || null })
								}
								groups={variableGroups}
								knownVariables={knownVariableNames}
							/>
						</div>
						<div className="col-6 col-md-3 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.MESSAGE_DATE")}
							</label>
							<InputText
								value={message.message_date ?? ""}
								onChange={(event) =>
									updateMessage(index, {
										message_date: event.target.value || null,
									})
								}
								placeholder="2025-01-01T10:00:00"
							/>
						</div>
						<div className="col-6 col-md-3 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.LIMIT")}
							</label>
							<InputText
								value={
									message.limit !== null && message.limit !== undefined
										? String(message.limit)
										: ""
								}
								keyfilter="pint"
								onChange={(event) =>
									updateMessage(index, {
										limit: event.target.value
											? Number(event.target.value)
											: null,
									})
								}
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.REF_TEMPLATE")}
							</label>
							<VariableInput
								value={message.ref_template ?? ""}
								onChange={(value) =>
									updateMessage(index, { ref_template: value || null })
								}
								groups={variableGroups}
								knownVariables={knownVariableNames}
								placeholder={t(
									"MARKETING_CAMPAIGNS.MESSAGES.REF_TEMPLATE_PLACEHOLDER"
								)}
							/>
						</div>
						<div className="col-12 col-md-6 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.SENDER")}
							</label>
							<VariableInput
								value={message.sender ?? ""}
								onChange={(value) =>
									updateMessage(index, { sender: value || null })
								}
								groups={variableGroups}
								knownVariables={knownVariableNames}
							/>
						</div>
						<div className="col-12 mc-field">
							<label className="mc-field-label">
								{t("MARKETING_CAMPAIGNS.MESSAGES.CONDITIONS")}
							</label>
							<ConditionsBuilder
								value={conditionsState.model}
								onChange={(model) =>
									updateMessage(index, { __conditionsModel: model })
								}
								rawYaml={conditionsState.text}
								onRawYamlChange={(text) =>
									updateMessage(index, { __conditionsText: text })
								}
								isAdvanced={conditionsState.advanced}
								onToggleAdvanced={(advanced) =>
									updateMessage(index, { __conditionsAdvanced: advanced })
								}
								fields={participantVariables}
								knownVariables={knownVariableNames}
							/>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderMessagesTab = () => {
		if (!config) return null;
		return (
			<div className="mc-messages">
				{config.messages.length === 0 && (
					<p className="mc-field-help">{t("MARKETING_CAMPAIGNS.MESSAGES.EMPTY")}</p>
				)}
				{config.messages.map((message, index) => renderMessage(message, index))}
				<UiButton
					type="button"
					className="p-button-rounded p-button-outlined mc-add-message"
					icon="pi pi-plus"
					label="MARKETING_CAMPAIGNS.MESSAGES.ADD"
					onClick={addMessage}
				/>
			</div>
		);
	};

	// Loading / not-found guard.
	if (!draft || !config) {
		const notFound =
			!isNewRoute &&
			campaigns.data !== undefined &&
			(numericId === null ||
				Number.isNaN(numericId) ||
				!campaigns.data.some((campaign) => campaign.id === numericId));
		return (
			<div
				className="mc-page"
				id={props.id}
			>
				<Toast ref={toast} />
				<ConfirmDialog />
				<UiCard>
					<div className="mc-empty-editor">
						<i
							className={
								notFound
									? "pi pi-exclamation-triangle mc-empty-icon"
									: "pi pi-spin pi-spinner mc-empty-icon"
							}
						/>
						<p>
							{notFound
								? t("MARKETING_CAMPAIGNS.EDITOR.NOT_FOUND")
								: t("MARKETING_CAMPAIGNS.EDITOR.LOADING")}
						</p>
						<UiButton
							type="button"
							className="p-button-rounded p-button-outlined"
							icon="pi pi-arrow-left"
							label="MARKETING_CAMPAIGNS.ACTIONS.BACK"
							onClick={goBackToList}
						/>
					</div>
				</UiCard>
			</div>
		);
	}

	return (
		<div
			className="mc-page"
			id={props.id}
		>
			<Toast ref={toast} />
			<ConfirmDialog />
			<UiCard
				title={
					isNew
						? "MARKETING_CAMPAIGNS.EDITOR.TITLE_NEW"
						: "MARKETING_CAMPAIGNS.EDITOR.TITLE_EDIT"
				}
				customHeader={renderHeader()}
			>
				<TabView className="mc-editor-tabs">
					<TabPanel header={t("MARKETING_CAMPAIGNS.EDITOR.TAB_CONFIG")}>
						{renderConfigTab()}
					</TabPanel>
					<TabPanel
						header={`${t("MARKETING_CAMPAIGNS.EDITOR.TAB_MESSAGES")} (${config.messages.length})`}
					>
						{renderMessagesTab()}
					</TabPanel>
					<TabPanel header={t("MARKETING_CAMPAIGNS.EDITOR.TAB_TESTING")}>
						{renderTestingTab()}
					</TabPanel>
				</TabView>

				{saveMessage && (
					<div className="form-message">
						<UiFieldMessage
							severity={FieldMessageSeverity.DANGER}
							label={saveMessage}
						/>
					</div>
				)}

				<div className="mc-actions">
					<UiButton
						type="button"
						className="p-button-rounded"
						icon="pi pi-save"
						label="MARKETING_CAMPAIGNS.ACTIONS.SAVE"
						onClick={onSave}
						loading={isSaving}
						disabled={!isDirty || isSaving}
					/>
				</div>
			</UiCard>
		</div>
	);
};

export default SettingsMarketingCampaignEdit;
